import { useEffect, useMemo, useRef, useState } from 'react';
import { escucharTabla, supabase, GUARDAR_USUARIO_FUNCTION_URL, LISTAR_USUARIOS_FUNCTION_URL, LOGIN_FUNCTION_URL, SUPABASE_ANON_KEY } from './supabase';
import { accionDe, entidadDe, normalizarEvento } from './auditoria';
import type { AccionAuditoria, AuditEvent, CambioAuditoria, Consumo, Notificaciones, Perfil, Preferencias, RolPermiso, Sucursal, Usuario } from './types';

// Lo que un módulo aporta al registrar un evento, además de la frase. Todo
// opcional: sin nada, el evento se guarda igual y la acción y la entidad se
// deducen de la frase.
export interface DetalleAuditoria {
  accion?: AccionAuditoria;
  entidad?: string;
  registro?: string;
  cambios?: CambioAuditoria[];
}

// ── DATA LAYER SOBRE TABLAS REALES DE SUPABASE ────────────────────────────────
// Cada entidad vive en su propia tabla. Mapeamos camelCase (app) <-> snake_case
// (Postgres). localStorage se usa SOLO como caché para render instantáneo y
// respaldo offline; la fuente de verdad es Supabase.

type Row = Record<string, any>;
function readCache<T>(key: string): T[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T[] : null;
  } catch {
    return null;
  }
}
function writeCache<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {

    /* ignore */}
}
interface TableConfig<T> {
  table: string;
  localKey: string;
  mapRow: (row: Row) => T;
  toRow: (item: T) => Row;
  getId?: (item: T) => string;
  seed?: T[];
  orderBy?: {
    column: string;
    ascending?: boolean;
  };
}
export type ArraySetter<T> = React.Dispatch<React.SetStateAction<T[]>>;

// Un fallo al escribir en Supabase no puede pasar en silencio: la app guarda
// primero en localStorage, así que sin aviso todo "parece" guardado y el
// cambio se pierde en cuanto se abre desde otro navegador. Antes el catch
// estaba vacío y ni siquiera se miraba el `error` que devuelve el cliente
// (upsert no lanza excepción: reporta el error en la respuesta).
const tablasAvisadas = new Set<string>();
function avisarFalloGuardado(tabla: string, motivo: string) {
  console.warn(`[db] No se pudo guardar "${tabla}" en Supabase: ${motivo}`);
  // Un aviso por tabla y por sesión: suficiente para enterarse, sin
  // interrumpir en cada tecla.
  if (tablasAvisadas.has(tabla)) return;
  tablasAvisadas.add(tabla);
  alert(`Los cambios en "${tabla}" no se guardaron en la base de datos.\n\n` + `Motivo: ${motivo}\n\n` + `Quedaron guardados solo en este navegador. Revisa la conexión y las ` + `politicas RLS de esa tabla en Supabase.`);
}

// Hook genérico tipo useState que sincroniza un arreglo con una tabla.
function useSupabaseTable<T>(cfg: TableConfig<T>): [T[], ArraySetter<T>] {
  const getId = cfg.getId ?? ((x: any) => x.id as string);
  const [items, setItems] = useState<T[]>(() => readCache<T>(cfg.localKey) ?? cfg.seed ?? []);
  const prevRef = useRef<T[]>(items);
  const ready = useRef(false);
  // Ids existentes al montar (caché/seed). Sirven para distinguir registros
  // que el usuario crea DURANTE la carga inicial (que sí hay que conservar)
  // de los datos viejos de caché (que el servidor debe sobrescribir).
  const initialIdsRef = useRef<Set<string>>(new Set(items.map(getId)));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let query = supabase.from(cfg.table).select('*');
        if (cfg.orderBy) {
          query = query.order(cfg.orderBy.column, {
            ascending: cfg.orderBy.ascending ?? true
          });
        }
        const {
          data,
          error
        } = await query;
        if (cancelled) return;
        if (error) {
          // Sin conexión / sin tabla: mantenemos la caché local.
          ready.current = true;
          return;
        }
        let rows = (data ?? []).map(cfg.mapRow);
        // Sembrar la tabla solo la primera vez (cuando está vacía).
        if (rows.length === 0 && cfg.seed && cfg.seed.length) {
          await supabase.from(cfg.table).upsert(cfg.seed.map(cfg.toRow));
          rows = cfg.seed;
        }
        // Fusionar: si el usuario creó registros mientras cargábamos (no estaban
        // en la caché inicial ni vienen del servidor), conservarlos y empujarlos
        // a Supabase en vez de descartarlos al sobrescribir con los del servidor.
        setItems(local => {
          const serverIds = new Set(rows.map(getId));
          const nuevosLocales = local.filter(it => !serverIds.has(getId(it)) && !initialIdsRef.current.has(getId(it)));
          if (nuevosLocales.length) {
            void supabase.from(cfg.table).upsert(nuevosLocales.map(cfg.toRow));
          }
          const merged = [...rows, ...nuevosLocales];
          prevRef.current = merged;
          writeCache(cfg.localKey, merged);
          return merged;
        });
      } catch {

        /* offline: caché */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.table]);

  // Tiempo real: lo que otro usuario guarde entra por aquí y se aplica sin
  // recargar. Se actualiza también `prevRef`, que es contra lo que se compara
  // al guardar para saber qué borrar: si no, un registro creado por otro
  // llegaría a la lista pero no a esa referencia, y el primer guardado que
  // hiciera este usuario lo daría por eliminado y lo borraría del servidor.
  useEffect(() => {
    return escucharTabla(cfg.table, ({
      tipo,
      fila,
      filaVieja
    }) => {
      const id = (fila?.id ?? filaVieja?.id) as string | undefined;
      if (!id) return;
      setItems(actual => {
        let next: T[];
        if (tipo === 'DELETE') {
          if (!actual.some(x => getId(x) === id)) return actual;
          next = actual.filter(x => getId(x) !== id);
        } else {
          const item = cfg.mapRow(fila);
          next = actual.some(x => getId(x) === id) ? actual.map(x => getId(x) === id ? item : x) : [...actual, item];
        }
        prevRef.current = next;
        writeCache(cfg.localKey, next);
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.table]);
  const sync = async (prev: T[], next: T[]) => {
    try {
      // upsert de todo lo presente (idempotente)
      if (next.length) {
        const {
          error
        } = await supabase.from(cfg.table).upsert(next.map(cfg.toRow));
        if (error) return avisarFalloGuardado(cfg.table, error.message);
      }
      // eliminar lo que ya no está
      const nextIds = new Set(next.map(getId));
      const removed = prev.map(getId).filter(id => !nextIds.has(id));
      if (removed.length) {
        const {
          error
        } = await supabase.from(cfg.table).delete().in('id', removed);
        if (error) return avisarFalloGuardado(cfg.table, error.message);
      }
    } catch (e) {
      // Sin conexión: la caché local ya quedó actualizada y se reintenta al
      // recargar. Se avisa igual para que nadie crea que quedó en la nube.
      avisarFalloGuardado(cfg.table, e instanceof Error ? e.message : 'sin conexión');
    }
  };
  const update: ArraySetter<T> = v => {
    setItems(prev => {
      const next = typeof v === 'function' ? (v as (p: T[]) => T[])(prev) : v;
      writeCache(cfg.localKey, next);
      if (ready.current) void sync(prevRef.current, next);
      prevRef.current = next;
      return next;
    });
  };
  return [items, update];
}

// ── HOOKS POR ENTIDAD ─────────────────────────────────────────────────────────

export function useSucursales(seed: Sucursal[]) {
  return useSupabaseTable<Sucursal>({
    table: 'sucursales',
    localKey: 'getr.sucursales',
    seed,
    mapRow: r => ({
      id: r.id,
      nombre: r.nombre ?? '',
      direccion: r.direccion ?? '',
      tipo: r.tipo ?? '',
      creado: Number(r.creado ?? Date.now()),
      archivado: !!r.archivado
    }),
    toRow: s => ({
      id: s.id,
      nombre: s.nombre,
      direccion: s.direccion,
      tipo: s.tipo,
      creado: s.creado,
      archivado: !!s.archivado
    })
  });
}

// ── USUARIOS: caso especial, sin acceso directo por RLS ──────────────────────
// La tabla `usuarios` tiene RLS estricto (ver RLS_USUARIOS_SQL): el cliente
// anónimo NO puede hacer select/insert/update directo. Todo pasa por Edge
// Functions ('login' para autenticar, 'guardar-usuario' para crear/editar),
// que usan la service_role key solo disponible en el servidor.
//
// Por eso este hook no reusa useSupabaseTable: en vez de eso, expone
// `guardarUsuario` (llama a la Edge Function) además del setter normal,
// que solo debe usarse para cambios que NO tocan password (archivar, etc.)
// y que de todas formas Supabase rechazará si el RLS está bien configurado.
export function useUsuarios(seed: Usuario[]) {
  const [items, setItems] = useState<Usuario[]>(() => readCache<Usuario>('getr.usuarios') ?? seed);
  const cargar = async () => {
    try {
      const res = await fetch(LISTAR_USUARIOS_FUNCTION_URL, {
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        }
      });
      if (!res.ok) return;
      const data = await res.json();
      const rows: Row[] = Array.isArray(data?.usuarios) ? data.usuarios : [];
      const mapped: Usuario[] = rows.map(r => ({
        id: r.id,
        nombre: r.nombre ?? '',
        correo: r.correo ?? '',
        rol: (r.rol ?? 'tecnico') as Usuario['rol'],
        sucursalesIds: Array.isArray(r.sucursales_ids) ? r.sucursales_ids : [],
        archivado: !!r.archivado
      }));
      if (mapped.length) {
        setItems(mapped);
        writeCache('getr.usuarios', mapped);
      }
    } catch {

      /* offline: nos quedamos con la caché/seed local */}
  };
  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tiempo real, por un camino distinto al de las demás tablas. `usuarios`
  // tiene RLS estricto y el cliente anónimo no puede leerla: los avisos de
  // tiempo real respetan ese RLS, así que aunque se metiera la tabla en la
  // publicación no llegaría nada. Y tampoco convendría: ahí están los hashes
  // de las contraseñas.
  //
  // En su lugar, quien guarda un usuario deja una marca en `app_state` (que sí
  // es pública) y los demás, al verla, vuelven a pedir la lista por la Edge
  // Function. Por el canal solo viaja un sello de tiempo, nunca datos de
  // usuarios.
  useEffect(() => {
    return escucharTabla('app_state', ({
      tipo,
      fila
    }) => {
      if (tipo === 'DELETE' || fila?.key !== CLAVE_AVISO_USUARIOS) return;
      void cargar();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    writeCache('getr.usuarios', items);
  }, [items]);
  return [items, setItems] as [Usuario[], ArraySetter<Usuario>];
}

// Crea o edita un usuario llamando a la Edge Function 'guardar-usuario'.
// La contraseña en texto plano (si se pasa) viaja por HTTPS y se hashea
// en el servidor — nunca se guarda ni se muestra en claro en el cliente.
// Clave de `app_state` que se toca al guardar un usuario. No guarda datos:
// solo un sello de tiempo que sirve de aviso a las demás sesiones.
const CLAVE_AVISO_USUARIOS = 'getr.usuarios.cambio';
export async function guardarUsuario(input: {
  id: string;
  nombre: string;
  correo: string;
  password?: string;
  rol: Usuario['rol'];
  sucursalesIds: string[];
  archivado?: boolean;
}): Promise<{
  ok: boolean;
  error?: string;
}> {
  try {
    const res = await fetch(GUARDAR_USUARIO_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(input)
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        error: data?.error ?? 'No se pudo guardar el usuario.'
      };
    }
    // Avisar a las demás sesiones de que la lista de usuarios cambió.
    // Si esto falla no se deshace nada: el usuario ya quedó guardado y los
    // demás lo verán en cuanto recarguen.
    void supabase.from('app_state').upsert({
      key: CLAVE_AVISO_USUARIOS,
      value: {
        at: new Date().toISOString()
      },
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'key'
    }).then(() => undefined, () => undefined);
    return {
      ok: true
    };
  } catch {
    return {
      ok: false,
      error: 'Sin conexión al servidor.'
    };
  }
}
// Comprueba una contraseña contra el servidor sin abrir sesión: se pregunta a
// la misma Edge Function del login. La contraseña real nunca sale de ahí (está
// cifrada en la base), así que no hay forma de compararla en el navegador; hay
// que preguntar a quien sí puede.
export async function verificarPassword(correo: string, password: string): Promise<boolean> {
  try {
    const res = await fetch(LOGIN_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        correo: correo.trim(),
        password
      })
    });
    const data = await res.json();
    return res.ok && !!data?.usuario;
  } catch {
    return false;
  }
}

// Cambia la contraseña del usuario que tiene la sesión abierta.
//
// Se exige la contraseña actual y se comprueba CONTRA EL SERVIDOR antes de
// tocar nada: si alguien deja la sesión abierta en un equipo, que no pueda
// cambiarla sin saber la que hay. Y solo se manda la nueva si la actual
// resultó correcta.
export async function cambiarPassword(input: {
  id: string;
  nombre: string;
  correo: string;
  rol: Usuario['rol'];
  sucursalesIds: string[];
  archivado?: boolean;
  passwordActual: string;
  passwordNueva: string;
}): Promise<{
  ok: boolean;
  error?: string;
}> {
  const correcta = await verificarPassword(input.correo, input.passwordActual);
  if (!correcta) {
    return {
      ok: false,
      error: 'La contraseña actual no es correcta.'
    };
  }
  return guardarUsuario({
    id: input.id,
    nombre: input.nombre,
    correo: input.correo,
    rol: input.rol,
    sucursalesIds: input.sucursalesIds,
    archivado: input.archivado,
    password: input.passwordNueva
  });
}
export function useRoles(seed: RolPermiso[]) {
  return useSupabaseTable<RolPermiso>({
    table: 'roles',
    localKey: 'getr.roles',
    seed,
    mapRow: r => ({
      id: r.id,
      nombre: r.nombre ?? '',
      descripcion: r.descripcion ?? '',
      permisos: r.permisos ?? {},
      archivado: !!r.archivado
    }),
    toRow: r => ({
      id: r.id,
      nombre: r.nombre,
      descripcion: r.descripcion,
      permisos: r.permisos,
      archivado: !!r.archivado
    })
  });
}

// Consumo NO se siembra: empieza vacío para que entres tus lecturas reales.
export function useConsumos() {
  return useSupabaseTable<Consumo>({
    table: 'consumo',
    localKey: 'getr.consumos',
    orderBy: {
      column: 'fecha',
      ascending: true
    },
    mapRow: r => ({
      id: r.id,
      sucursalId: r.sucursal_id ?? '',
      fecha: Number(r.fecha ?? Date.now()),
      kwh: Number(r.kwh ?? 0),
      archivado: !!r.archivado
    }),
    toRow: c => ({
      id: c.id,
      sucursal_id: c.sucursalId,
      fecha: c.fecha,
      kwh: c.kwh,
      archivado: !!c.archivado
    })
  });
}

// ── AUDITORÍA (log append-only) ───────────────────────────────────────────────
// El log se lee completo: no se limita la consulta ni se recorta la lista.
// Nota de crecimiento: la tabla `auditoria` solo crece, así que si algún día
// llega a decenas de miles de filas esta carga se va a notar. Cuando pase,
// el paso siguiente es paginar contra el servidor (leer por rangos de id) en
// vez de traerlo todo; hoy el volumen no lo justifica y la contrapartida de
// limitar es peor: perder de vista el historial.
//
// `actor` es una función y no un valor porque `useAuditoria` se monta antes de
// que exista la sesión: se consulta en el momento de registrar, no en el de
// montar.
export function useAuditoria(stamp: () => string, actor: () => {
  id: string;
  nombre: string;
} | null) {
  // Se guardan las filas crudas y los eventos se derivan: así la caché, el
  // aviso de tiempo real y lo que se acaba de registrar hablan todos el mismo
  // idioma, sin convertir de ida y vuelta.
  const [filas, setFilas] = useState<Row[]>(() => readCache<Row>('getr.auditoria') ?? []);
  const audit = useMemo(() => filas.map(normalizarEvento), [filas]);
  const ready = useRef(false);

  // Las columnas de detalle se agregaron después (sql/auditoria_detallada.sql).
  // Si el proyecto todavía no las tiene, pedirlas devuelve error y la pantalla
  // se quedaría vacía; por eso se reintenta con las de siempre. Se recuerda el
  // resultado para no mandar dos consultas cada vez que se registra algo.
  const columnasNuevas = useRef(true);
  const COMPLETAS = 'id, mensaje, fecha, usuario, usuario_id, accion, entidad, registro, cambios';
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pedir = (columnas: string) => supabase.from('auditoria').select(columnas).order('id', {
          ascending: false
        });
        let respuesta = (await pedir(COMPLETAS)) as {
          data: Row[] | null;
          error: unknown;
        };
        if (respuesta.error) {
          columnasNuevas.current = false;
          respuesta = (await pedir('id, mensaje')) as {
            data: Row[] | null;
            error: unknown;
          };
        }
        if (cancelled) return;
        if (!respuesta.error && respuesta.data) {
          const list = respuesta.data;
          setFilas(list);
          writeCache('getr.auditoria', list);
        }
      } catch {

        /* offline */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const agregar = (fila: Row) => setFilas(prev => {
    // Sin recorte: un registro de auditoría que descarta lo viejo deja de
    // servir para auditar. Antes se quedaba con los últimos 80, tanto aquí
    // como al leer de Supabase, así que el historial anterior era invisible
    // aunque estuviera guardado en la tabla.
    const next = [fila, ...prev];
    writeCache('getr.auditoria', next);
    return next;
  });

  // Tiempo real: lo que registre otro usuario aparece arriba al momento.
  // `log()` ya escribe la línea en la lista local antes de mandarla al
  // servidor, así que el aviso de vuelta la duplicaría; se anota lo propio en
  // `propias` y se descarta ese único eco. No se descarta por texto sin más
  // porque dos usuarios pueden registrar lo mismo en el mismo minuto y esa sí
  // hay que verla dos veces.
  const propias = useRef<string[]>([]);
  useEffect(() => {
    return escucharTabla('auditoria', ({
      tipo,
      fila
    }) => {
      if (tipo !== 'INSERT') return;
      const linea = fila?.mensaje as string | undefined;
      if (!linea) return;
      const i = propias.current.indexOf(linea);
      if (i >= 0) {
        propias.current.splice(i, 1);
        return;
      }
      agregar(fila as Row);
    });
  }, []);

  // `detalle` es opcional para no obligar a cada sitio que registra algo a
  // saber de esto: sin él el evento sigue guardándose con su frase y su autor,
  // que ya es más de lo que había antes.
  const log = (msg: string, detalle?: DetalleAuditoria) => {
    const full = `${stamp()} - ${msg}`;
    const quien = actor();
    const fila: Row = {
      mensaje: full,
      usuario: quien?.nombre ?? '',
      usuario_id: quien?.id ?? '',
      accion: detalle?.accion ?? accionDe(msg),
      entidad: detalle?.entidad ?? entidadDe(msg),
      registro: detalle?.registro ?? '',
      cambios: detalle?.cambios ?? []
    };
    propias.current.push(full);
    // La fecha exacta la pone el servidor (`default now()`), y a propósito: es
    // la única de las dos que no depende del reloj del navegador de quien hizo
    // la acción. Aquí se pone una local nada más para que la fila que se acaba
    // de agregar no se vea sin fecha; al recargar llega la del servidor y la
    // reemplaza.
    agregar({
      ...fila,
      fecha: new Date().toISOString()
    });

    // Si el proyecto todavía no tiene las columnas nuevas, el insert completo
    // falla entero y el evento se perdería. Se reintenta con la columna de
    // siempre: mejor un evento sin detalle que ningún evento.
    void (async () => {
      try {
        if (columnasNuevas.current) {
          const {
            error
          } = await supabase.from('auditoria').insert(fila);
          if (!error) return;
          columnasNuevas.current = false;
        }
        await supabase.from('auditoria').insert({
          mensaje: full
        });
      } catch {

        /* offline: queda en la caché local */}
    })();
  };
  return {
    audit,
    log
  };
}

// ── CONFIGURACIÓN (fila única) ────────────────────────────────────────────────
interface ConfigState {
  perfil: Perfil;
  notif: Notificaciones;
  prefs: Preferencias;
}
export function useConfiguracion(defaults: ConfigState, userId: string) {
  const cacheKey = `getr.perfil.${userId}`;
  const cacheKeyNotif = `getr.notificaciones.${userId}`;
  const cacheKeyPrefs = `getr.preferencias.${userId}`;
  const [perfil, setPerfilState] = useState<Perfil>(() => readCache<Perfil>(cacheKey)?.[0] ?? defaults.perfil);
  const [notif, setNotifState] = useState<Notificaciones>(() => readCache<Notificaciones>(cacheKeyNotif)?.[0] ?? defaults.notif);
  const [prefs, setPrefsState] = useState<Preferencias>(() => readCache<Preferencias>(cacheKeyPrefs)?.[0] ?? defaults.prefs);
  const ready = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<ConfigState>({
    perfil,
    notif,
    prefs
  });
  latest.current = {
    perfil,
    notif,
    prefs
  };
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('configuracion').select('*').eq('id', userId).maybeSingle();
        if (cancelled) return;
        if (!error) {
          if (data) {
            if (data.perfil) setPerfilState(data.perfil);
            if (data.notificaciones) setNotifState(data.notificaciones);
            if (data.preferencias) setPrefsState(data.preferencias);
          } else {
            // Sembrar fila por usuario.
            await supabase.from('configuracion').upsert({
              id: userId,
              perfil: defaults.perfil,
              notificaciones: defaults.notif,
              preferencias: defaults.prefs
            });
          }
        }
      } catch {

        /* offline */} finally {
        ready.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Tiempo real de la propia configuración: cambiar el perfil en una pantalla
  // se refleja en las otras sesiones del mismo usuario. Solo interesa la fila
  // propia, y se ignora mientras haya un guardado pendiente: si no, el eco de
  // lo que se acaba de escribir volvería a caer encima de lo que se está
  // escribiendo ahora mismo.
  useEffect(() => {
    if (!userId) return;
    return escucharTabla('configuracion', ({
      tipo,
      fila
    }) => {
      if (tipo === 'DELETE' || fila?.id !== userId) return;
      if (timer.current) return;
      if (fila.perfil) setPerfilState(fila.perfil);
      if (fila.notificaciones) setNotifState(fila.notificaciones);
      if (fila.preferencias) setPrefsState(fila.preferencias);
    });
  }, [userId]);
  const persist = () => {
    if (!ready.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void supabase.from('configuracion').upsert({
        id: userId,
        perfil: latest.current.perfil,
        notificaciones: latest.current.notif,
        preferencias: latest.current.prefs
      });
    }, 400);
  };
  const setPerfil = (value: Perfil) => {
    setPerfilState(value);
    writeCache(cacheKey, [value]);
    persist();
  };
  const setNotif = (value: Notificaciones) => {
    setNotifState(value);
    writeCache(cacheKeyNotif, [value]);
    persist();
  };
  const setPrefs = (value: Preferencias) => {
    setPrefsState(value);
    writeCache(cacheKeyPrefs, [value]);
    persist();
  };
  return {
    perfil,
    notif,
    prefs,
    setPerfil,
    setNotif,
    setPrefs
  };
}